import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function ResponsiveTable({ children, ...tableProps }) {
  return <div className="workspace-table responsive-table"><table {...tableProps}>{children}</table></div>;
}

// ========== Перетаскиваемый блок ==========
function SortableItem({ id, wide, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    border: '1px solid #ccc',
    borderRadius: '8px',
    padding: '10px',
    minWidth: 0,
    background: 'white',
    ...(wide ? { gridColumn: '1 / -1' } : {}),
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

// ========== Вкладка Дашборд ==========
function DashboardTab({ invoices = [], payments = [], suppliers = [], eurRate, setEurRate, usdRate, setUsdRate, balanceDate, setBalanceDate }) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [collapsedBlocks, setCollapsedBlocks] = useState({});

  const countryKeys = useMemo(() => [...new Set(suppliers.map(s => s.country || 'Другая'))].sort(), [suppliers]);
  const defaultCountryOrder = countryKeys;

  const savedCountries = JSON.parse(localStorage.getItem('dashboardCountriesOrder') || 'null');
  const validCountries = savedCountries && countryKeys.every(k => savedCountries.includes(k))
    ? savedCountries
    : defaultCountryOrder;
  const [countryOrder, setCountryOrder] = useState(validCountries);

  useEffect(() => {
    const missing = countryKeys.filter(k => !countryOrder.includes(k));
    const extra = countryOrder.filter(k => !countryKeys.includes(k));
    if (missing.length > 0 || extra.length > 0) {
      let newOrder = countryOrder.filter(k => countryKeys.includes(k));
      for (const k of missing) newOrder.push(k);
      setCountryOrder(newOrder);
    }
  }, [countryKeys, countryOrder]);

  const toggleCollapse = (key, e) => {
    e.stopPropagation();
    setCollapsedBlocks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ========== Сводная таблица ==========
  const buildSummaryTable = () => {
    const months = ["Январь","Февраль","Март","Апрель","Май","Июнь",
                    "Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
    const currencies = ["USD", "EUR", "RUB"];
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const weekAgoStr = new Date(now.getTime() - 7*24*3600*1000).toISOString().slice(0, 10);

    const rows = [];
    const rowLabels = ["Сегодня", "Текущая неделя", ...months];

    for (const label of rowLabels) {
      const row = { label };
      for (const curr of currencies) {
        let invSum = 0, paySum = 0;
        const invs = invoices.filter(i => i.currency === curr);
        const pays = payments.filter(p => p.currency === curr);

        if (label === "Сегодня") {
          invSum = invs.filter(i => i.invoice_date?.startsWith(todayStr)).reduce((s,i)=>s+i.total_amount,0);
          paySum = pays.filter(p => p.payment_date?.startsWith(todayStr)).reduce((s,p)=>s+p.amount,0);
        } else if (label === "Текущая неделя") {
          invSum = invs.filter(i => i.invoice_date >= weekAgoStr && i.invoice_date <= todayStr).reduce((s,i)=>s+i.total_amount,0);
          paySum = pays.filter(p => p.payment_date >= weekAgoStr && p.payment_date <= todayStr).reduce((s,p)=>s+p.amount,0);
        } else {
          const monthIdx = months.indexOf(label);
          invSum = invs.filter(i => new Date(i.invoice_date).getMonth() === monthIdx && new Date(i.invoice_date).getFullYear() === selectedYear).reduce((s,i)=>s+i.total_amount,0);
          paySum = pays.filter(p => new Date(p.payment_date).getMonth() === monthIdx && new Date(p.payment_date).getFullYear() === selectedYear).reduce((s,p)=>s+p.amount,0);
        }
        const balance = paySum - invSum;
        row[curr] = { inv: invSum, pay: paySum, bal: balance };
      }
      rows.push(row);
    }
    return rows;
  };

  const summaryRows = buildSummaryTable();

  // ========== Балансы по странам ==========
  const balancesByCountry = () => {
    const map = {};
    suppliers.forEach(s => {
      const invSum = invoices.filter(i => i.supplier_id === s.id).reduce((sum,i)=>sum+i.total_amount,0);
      const paySum = payments.filter(p => p.supplier_id === s.id).reduce((sum,p)=>sum+p.amount,0);
      const balance = paySum - invSum;
      const country = s.country || 'Другая';
      if (!map[country]) map[country] = [];
      map[country].push({ name: s.name, currency: s.currency, balance });
    });
    return map;
  };

  const countryBalances = balancesByCountry();

  // ========== Drag-and-drop для стран ==========
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setCountryOrder((prevOrder) => {
        const oldIndex = prevOrder.indexOf(active.id);
        const newIndex = prevOrder.indexOf(over.id);
        const newOrder = arrayMove(prevOrder, oldIndex, newIndex);
        localStorage.setItem('dashboardCountriesOrder', JSON.stringify(newOrder));
        return newOrder;
      });
    }
  };

  const renderCountryBlock = (key) => {
    const supps = countryBalances[key] || [];
    return (
      <SortableItem key={key} id={key}>
        <h4 onMouseDown={(e) => toggleCollapse(key, e)} style={{ cursor: 'pointer', margin: 0 }}>
          {collapsedBlocks[key] ? '▶' : '▼'} {key} ({supps.length} поставщиков)
        </h4>
        {!collapsedBlocks[key] && (
          <ResponsiveTable border="1" cellPadding="5" style={{ width: '100%', marginTop: '8px' }}>
            <thead><tr><th>Поставщик</th><th>Баланс</th></tr></thead>
            <tbody>
              {supps.map(s => (
                <tr key={s.name}><td>{s.name} ({s.currency})</td><td>{s.balance.toFixed(2)} {s.currency}</td></tr>
              ))}
            </tbody>
          </ResponsiveTable>
        )}
      </SortableItem>
    );
  };

  return (
    <div>
      <h3>Дашборд</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '10px' }}>
        <label>Курс €: <input type="number" value={eurRate} onChange={e => setEurRate(parseFloat(e.target.value) || 0)} /></label>
        <label>Курс $: <input type="number" value={usdRate} onChange={e => setUsdRate(parseFloat(e.target.value) || 0)} /></label>
        <label>Год:
          <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
            {[currentYear-1, currentYear, currentYear+1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label>Дата баланса: <input type="date" value={balanceDate} onChange={e => setBalanceDate(e.target.value)} /></label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: '16px' }}>
        {/* Закреплённая сводка */}
        <div style={{ gridColumn: '1 / -1', minWidth: 0, border: '1px solid #ccc', borderRadius: '8px', padding: '10px', background: 'white' }}>
          <h4 onMouseDown={(e) => toggleCollapse('summary', e)} style={{ cursor: 'pointer', margin: 0 }}>
            {collapsedBlocks['summary'] ? '▶' : '▼'} Сводка по всем валютам (год: {selectedYear})
          </h4>
          {!collapsedBlocks['summary'] && (
            <ResponsiveTable border="1" cellPadding="5" style={{ width: '100%', fontSize: '0.9em', marginTop: '8px' }}>
              <thead>
                <tr>
                  <th></th>
                  <th colSpan="3">USD</th>
                  <th colSpan="3">EUR</th>
                  <th colSpan="3">RUB</th>
                </tr>
                <tr>
                  <th></th>
                  <th>Инвойсов</th><th>Оплат</th><th>Сальдо</th>
                  <th>Инвойсов</th><th>Оплат</th><th>Сальдо</th>
                  <th>Инвойсов</th><th>Оплат</th><th>Сальдо</th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.label}</td>
                    {['USD','EUR','RUB'].map(curr => (
                      <React.Fragment key={curr}>
                        <td>{row[curr].inv.toFixed(2)}</td>
                        <td>{row[curr].pay.toFixed(2)}</td>
                        <td>{row[curr].bal.toFixed(2)}</td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          )}
        </div>

        {/* Перетаскиваемые блоки стран */}
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={countryOrder} strategy={rectSortingStrategy}>
            {countryOrder.map(key => renderCountryBlock(key))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

// ========== Главный компонент AdminDashboard ==========
function AdminDashboard({embedded = false, section = 'users'}) {
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  useEffect(() => { if (embedded) setActiveTab(section); }, [embedded, section]);

  // Фильтры
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterInvoiceNumber, setFilterInvoiceNumber] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterMinAmount, setFilterMinAmount] = useState('');
  const [filterMaxAmount, setFilterMaxAmount] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');

  // Пагинация
  const [invoicePage, setInvoicePage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const PER_PAGE = 50;

  const [newUser, setNewUser] = useState({
    username: '', password: '', role: 'purchaser',
    full_name: '', can_view_all: false,
    company_ids: [], consignee_ids: []
  });
  const [editingUser, setEditingUser] = useState(null);
  const [newSupplier, setNewSupplier] = useState({ name: '', country: '', currency: 'RUB', type: 'plantation' });
  const [newCompany, setNewCompany] = useState({ name: '' });
  const [newConsignee, setNewConsignee] = useState({ name: '' });
  const [newPayment, setNewPayment] = useState({ company_id: '', supplier_id: '', amount: '', comment: '', payment_date: '' });

  const [eurRate, setEurRate] = useState(90.0);
  const [usdRate, setUsdRate] = useState(75.0);
  const [balanceDate, setBalanceDate] = useState('');

  const [reportSupplier, setReportSupplier] = useState('');
  const [reportMonth, setReportMonth] = useState('');
  const [reportYear, setReportYear] = useState('');

  // ---------- Модальное редактирование инвойса ----------
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState({
    supplier_id: '', invoice_number: '', invoice_date: '', delivery_date: '', consignee: '',
    total_amount: '', upload_type: 'import', awb_number: '', transport_weight: '', transport_cost: '',
    supplier_commission: '', packaging_cost: '', packaging_details: [],
    items: [{
      flower_type: '', variety: '', length: '', stems_per_bunch: '',
      box_dimension: '', box_number: '', bunches_per_box: '', total_stems: '', price_per_stem: '', total_line: ''
    }]
  });

  const navigate = useNavigate();

  // Загрузка инвойсов с фильтрами и пагинацией
  const fetchInvoices = useCallback((additionalParams = {}) => {
    const params = {
      ...additionalParams,
      page: invoicePage,
      per_page: PER_PAGE,
    };
    api.get('/tenant/invoices', { params })
      .then(res => {
        setInvoices(res.data);
        const total = parseInt(res.headers['x-total-count'], 10) || 0;
        setTotalInvoices(total);
      })
      .catch(() => {});
  }, [invoicePage]);

  const fetchData = useCallback(() => {
    api.get('/tenant/users').then(res => setUsers(res.data));
    api.get('/tenant/suppliers').then(res => setSuppliers(res.data));
    api.get('/tenant/companies').then(res => setCompanies(res.data));
    api.get('/tenant/consignees').then(res => setConsignees(res.data));
    api.get('/tenant/payments').then(res => setPayments(res.data));
    fetchInvoices();
  }, [fetchInvoices]);

  // При монтировании загружаем всё
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMultiSelect = (e, setter) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) selected.push(parseInt(options[i].value));
    }
    setter(selected);
  };

  // ---------- Пользователи ----------
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tenant/users', newUser);
      alert('Пользователь создан');
      setNewUser({ username: '', password: '', role: 'purchaser', full_name: '', can_view_all: false, company_ids: [], consignee_ids: [] });
      fetchData();
    } catch (err) { alert('Ошибка'); }
  };

  const handleEditUser = (user) => {
    setEditingUser(user.id);
    setNewUser({
      username: user.username, password: '', role: user.role,
      full_name: user.full_name || '', can_view_all: user.can_view_all,
      company_ids: user.company_ids || [], consignee_ids: user.consignee_ids || [],
    });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      await api.put(`/tenant/users/${editingUser}`, newUser);
      alert('Пользователь обновлён');
      setEditingUser(null);
      setNewUser({ username: '', password: '', role: 'purchaser', full_name: '', can_view_all: false, company_ids: [], consignee_ids: [] });
      fetchData();
    } catch (err) { alert('Ошибка'); }
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setNewUser({ username: '', password: '', role: 'purchaser', full_name: '', can_view_all: false, company_ids: [], consignee_ids: [] });
  };

  // ---------- Поставщики ----------
  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    if (!newSupplier.name) return;
    try { await api.post('/tenant/suppliers', newSupplier); alert('Поставщик добавлен'); setNewSupplier({ name: '', country: '', currency: 'RUB', type: 'plantation' }); fetchData(); } catch (err) { alert('Ошибка'); }
  };

  const handleDeleteSupplier = async (id) => { try { await api.delete(`/tenant/suppliers/${id}`); fetchData(); } catch (err) { alert('Ошибка'); } };

  // ---------- Грузополучатели ----------
  const handleCreateConsignee = async (e) => {
    e.preventDefault();
    if (!newConsignee.name) return;
    try { await api.post('/tenant/consignees', newConsignee); alert('Грузополучатель добавлен'); setNewConsignee({ name: '' }); fetchData(); } catch (err) { alert('Ошибка'); }
  };

  const handleDeleteConsignee = async (id) => { try { await api.delete(`/tenant/consignees/${id}`); fetchData(); } catch (err) { alert('Ошибка'); } };

  // ---------- Компании ----------
  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name) return;
    try { await api.post('/tenant/companies', newCompany); alert('Компания создана'); setNewCompany({ name: '' }); fetchData(); } catch (err) { alert('Ошибка'); }
  };

  // ---------- Оплаты ----------
  const handleCreatePayment = async (e) => {
    e.preventDefault();
    try { await api.post('/tenant/payments', newPayment); alert('Оплата внесена'); setNewPayment({ company_id: '', supplier_id: '', amount: '', comment: '', payment_date: '' }); fetchData(); } catch (err) { alert('Ошибка'); }
  };

  // ---------- Инвойсы (удаление) ----------
  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Удалить инвойс?')) return;
    try { await api.delete(`/tenant/invoices/${id}`); fetchInvoices(); } catch (err) { alert('Ошибка'); }
  };

  // ---------- Модальное редактирование инвойса ----------
  const openEditModal = (invoice) => {
    setEditingInvoice(invoice);
    setEditForm({
      supplier_id: invoice.supplier_id || '',
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date || '',
      delivery_date: invoice.delivery_date || '',
      consignee: invoice.consignee || '',
      total_amount: invoice.total_amount?.toString() || '',
      upload_type: 'import',
      awb_number: invoice.awb_number || '',
      transport_weight: invoice.transport_weight?.toString() || '',
      transport_cost: invoice.transport_cost?.toString() || '',
      supplier_commission: invoice.supplier_commission?.toString() || '',
      packaging_cost: invoice.packaging_cost?.toString() || '',
      packaging_details: invoice.packaging_details || [],
      items: (invoice.items || []).map(item => ({
        flower_type: item.flower_type || '',
        variety: item.variety || '',
        length: item.length || '',
        stems_per_bunch: item.stems_per_bunch?.toString() || '',
        box_dimension: item.box_dimension || '',
        box_number: item.box_number || '',
        bunches_per_box: item.bunches_per_box?.toString() || '',
        total_stems: item.total_stems?.toString() || '',
        price_per_stem: item.price_per_stem?.toString() || '',
        total_line: item.total_line?.toString() || ''
      }))
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editForm.supplier_id) return;
    const payload = {
      company_id: localStorage.getItem('x_company_id'),
      supplier_id: editForm.supplier_id,
      invoice_number: editForm.invoice_number,
      invoice_date: editForm.invoice_date || null,
      delivery_date: editForm.delivery_date || null,
      consignee: editForm.consignee || '',
      total_amount: parseFloat(editForm.total_amount) || editForm.items.reduce((sum, item) => sum + parseFloat(item.total_line || 0), 0),
      upload_type: 'import',
      awb_number: editForm.awb_number,
      transport_weight: parseFloat(editForm.transport_weight) || null,
      transport_cost: parseFloat(editForm.transport_cost) || null,
      supplier_commission: parseFloat(editForm.supplier_commission) || null,
      packaging_cost: parseFloat(editForm.packaging_cost) || null,
      packaging_details: editForm.packaging_details,
      items: editForm.items.map(item => ({
        flower_type: item.flower_type || '',
        variety: item.variety || '',
        length: item.length || '',
        stems_per_bunch: parseInt(item.stems_per_bunch) || 0,
        box_dimension: item.box_dimension || '',
        box_number: item.box_number || '',
        bunches_per_box: parseInt(item.bunches_per_box) || 0,
        total_stems: parseInt(item.total_stems) || 0,
        price_per_stem: parseFloat(item.price_per_stem) || 0,
        total_line: parseFloat(item.total_line) || 0,
        unit: 'stem'
      }))
    };
    try {
      await api.put(`/tenant/invoices/${editingInvoice.id}`, payload);
      alert('Инвойс обновлён');
      setEditModalOpen(false);
      fetchInvoices();
    } catch (err) {
      alert(err.response?.data?.detail || 'Ошибка');
    }
  };

  // ============ рендер вкладок ============
  const renderTab = () => {
    switch (activeTab) {
      // ---------- Пользователи ----------
      case 'users':
        return (
          <div>
            <h3>{editingUser ? 'Редактирование пользователя' : 'Добавить пользователя'}</h3>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              <div className="invoice-fields-grid" style={{ gap: '10px', marginBottom: '10px' }}>
                <label>ФИО: <input value={newUser.full_name} onChange={e => setNewUser({...newUser, full_name: e.target.value})} /></label>
                <label>Логин: <input value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} required /></label>
                <label>Пароль: <input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required={!editingUser} /></label>
                <label>Роль:
                  <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}>
                    <option value="purchaser">Закупщик</option>
                    <option value="admin">Администратор</option>
                  </select>
                </label>
                <label>Видеть все операции:
                  <input type="checkbox" checked={newUser.can_view_all} onChange={e => setNewUser({...newUser, can_view_all: e.target.checked})} />
                </label>
              </div>
              <div className="invoice-fields-grid" style={{ gap: '10px' }}>
                <label>Компании (Ctrl+клик для нескольких):
                  <select multiple value={newUser.company_ids.map(String)} onChange={e => handleMultiSelect(e, (ids) => setNewUser({...newUser, company_ids: ids}))}>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label>Грузополучатели (Ctrl+клик для нескольких):
                  <select multiple value={newUser.consignee_ids.map(String)} onChange={e => handleMultiSelect(e, (ids) => setNewUser({...newUser, consignee_ids: ids}))}>
                    {consignees.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
              </div>
              <button type="submit">{editingUser ? 'Сохранить' : 'Создать'}</button>
              {editingUser && <button type="button" onClick={cancelEdit} style={{ marginLeft: '10px' }}>Отмена</button>}
            </form>

            <h3>Пользователи</h3>
            <ResponsiveTable border="1" cellPadding="5">
              <thead><tr><th>ФИО</th><th>Логин</th><th>Роль</th><th>Видеть все</th><th>Компании</th><th>Грузополучатели</th><th></th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}><td>{u.full_name || '—'}</td><td>{u.username}</td><td>{u.role}</td><td>{u.can_view_all ? 'Да' : 'Нет'}</td><td>{(u.company_ids || []).join(', ')}</td><td>{(u.consignee_ids || []).join(', ')}</td><td><button onClick={() => handleEditUser(u)}>Редактировать</button></td></tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
        );
      // ---------- Поставщики ----------
      case 'suppliers':
        return (
          <div>
            <h3>Добавить поставщика</h3>
            <form onSubmit={handleCreateSupplier}>
              <input placeholder="Название" value={newSupplier.name} onChange={e => setNewSupplier({...newSupplier, name: e.target.value})} required />
              <input placeholder="Страна" value={newSupplier.country} onChange={e => setNewSupplier({...newSupplier, country: e.target.value})} />
              <select value={newSupplier.currency} onChange={e => setNewSupplier({...newSupplier, currency: e.target.value})}>
                <option value="RUB">₽ RUB</option><option value="EUR">€ EUR</option><option value="USD">$ USD</option>
              </select>
              <select value={newSupplier.type} onChange={e => setNewSupplier({...newSupplier, type: e.target.value})}>
                <option value="plantation">Плантация</option><option value="truck">Трак</option><option value="transfer">Перевод</option>
              </select>
              <button type="submit">Добавить</button>
            </form>
            <h3>Действующие поставщики</h3>
            <ResponsiveTable border="1" cellPadding="5"><thead><tr><th>Название</th><th>Страна</th><th>Валюта</th><th>Тип</th><th></th></tr></thead>
              <tbody>{suppliers.map(s => (<tr key={s.id}><td>{s.name}</td><td>{s.country || '—'}</td><td>{s.currency}</td><td>{s.type || 'Плантация'}</td><td><button onClick={() => handleDeleteSupplier(s.id)}>Удалить</button></td></tr>))}</tbody>
            </ResponsiveTable>
          </div>
        );
      // ---------- Грузополучатели ----------
      case 'consignees':
        return (
          <div>
            <h3>Добавить грузополучателя</h3>
            <form onSubmit={handleCreateConsignee}><input placeholder="Название" value={newConsignee.name} onChange={e => setNewConsignee({...newConsignee, name: e.target.value})} required /><button type="submit">Добавить</button></form>
            <h3>Действующие грузополучатели</h3>
            <ResponsiveTable border="1" cellPadding="5"><thead><tr><th>Название</th><th></th></tr></thead>
              <tbody>{consignees.map(c => (<tr key={c.id}><td>{c.name}</td><td><button onClick={() => handleDeleteConsignee(c.id)}>Удалить</button></td></tr>))}</tbody>
            </ResponsiveTable>
          </div>
        );
      // ---------- Компании ----------
      case 'companies':
        return (
          <div>
            <h3>Создать компанию</h3>
            <form onSubmit={handleCreateCompany}><input placeholder="Название" value={newCompany.name} onChange={e => setNewCompany({...newCompany, name: e.target.value})} required /><button type="submit">Создать</button></form>
            <h3>Действующие компании</h3>
            <ul>{companies.map(c => <li key={c.id}>{c.name}</li>)}</ul>
          </div>
        );
      // ---------- Внесение оплаты ----------
      case 'payments':
        return (
          <div>
            <h3>Внести оплату</h3>
            <form onSubmit={handleCreatePayment}>
              <select value={newPayment.supplier_id} onChange={e => setNewPayment({...newPayment, supplier_id: e.target.value})} required><option value="">Поставщик</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.currency})</option>)}</select>
              <select value={newPayment.company_id} onChange={e => setNewPayment({...newPayment, company_id: e.target.value})} required><option value="">Компания</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
              <input type="number" placeholder="Сумма" value={newPayment.amount} onChange={e => setNewPayment({...newPayment, amount: e.target.value})} required />
              <input type="date" value={newPayment.payment_date} onChange={e => setNewPayment({...newPayment, payment_date: e.target.value})} />
              <input placeholder="Комментарий" value={newPayment.comment} onChange={e => setNewPayment({...newPayment, comment: e.target.value})} />
              <button type="submit">Внести</button>
            </form>
            <h4>История оплат</h4>
            <ResponsiveTable border="1" cellPadding="5"><thead><tr><th>Поставщик</th><th>Компания</th><th>Сумма</th><th>Дата</th><th>Комментарий</th></tr></thead>
              <tbody>{payments.map(p => (<tr key={p.id}><td>{p.supplier_name}</td><td>{p.company_name}</td><td>{p.amount}</td><td>{p.payment_date?.substring(0,10)}</td><td>{p.comment}</td></tr>))}</tbody>
            </ResponsiveTable>
          </div>
        );
      // ---------- Инвойсы ----------
      case 'invoices':
        const handleFilter = () => {
          setInvoicePage(1); // сброс на первую страницу
          const params = {};
          if (filterSupplier) params.supplier_id = filterSupplier;
          if (filterInvoiceNumber) params.invoice_number = filterInvoiceNumber;
          if (filterCountry) params.country = filterCountry;
          if (filterStartDate) params.start_date = filterStartDate;
          if (filterEndDate) params.end_date = filterEndDate;
          if (filterMinAmount) params.min_amount = filterMinAmount;
          if (filterMaxAmount) params.max_amount = filterMaxAmount;
          if (filterCurrency) params.currency = filterCurrency;
          fetchInvoices(params);
        };

        const totalPages = Math.ceil(totalInvoices / PER_PAGE) || 1;

        return (
          <div>
            <h3>Инвойсы</h3>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <select value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)} style={{ width: '150px' }}>
                <option value="">Все поставщики</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input placeholder="Номер инвойса" value={filterInvoiceNumber} onChange={e => setFilterInvoiceNumber(e.target.value)} style={{ width: '130px' }} />
              <input placeholder="Страна" value={filterCountry} onChange={e => setFilterCountry(e.target.value)} style={{ width: '100px' }} />
              <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} style={{ width: '130px' }} />
              <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} style={{ width: '130px' }} />
              <input type="number" placeholder="Мин. сумма" value={filterMinAmount} onChange={e => setFilterMinAmount(e.target.value)} style={{ width: '100px' }} />
              <input type="number" placeholder="Макс. сумма" value={filterMaxAmount} onChange={e => setFilterMaxAmount(e.target.value)} style={{ width: '100px' }} />
              <select value={filterCurrency} onChange={e => setFilterCurrency(e.target.value)} style={{ width: '80px' }}>
                <option value="">Валюта</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="RUB">RUB</option>
              </select>
              <button onClick={handleFilter} style={{ padding: '4px 8px', cursor: 'pointer' }}>🔍 Фильтр</button>
              <button onClick={() => { 
                setFilterSupplier(''); setFilterInvoiceNumber(''); setFilterCountry(''); 
                setFilterStartDate(''); setFilterEndDate(''); setFilterMinAmount(''); 
                setFilterMaxAmount(''); setFilterCurrency(''); 
                setInvoicePage(1);
                fetchInvoices(); 
              }} style={{ padding: '4px 8px', cursor: 'pointer' }}>Очистить</button>
            </div>
            <ResponsiveTable border="1" cellPadding="5">
              <thead><tr><th>Дата</th><th>Поставщик</th><th>Номер</th><th>Сумма</th><th>Валюта</th><th>Страна</th><th></th></tr></thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id}>
                    <td>{inv.invoice_date || '—'}</td>
                    <td>{inv.supplier_name}</td>
                    <td>{inv.invoice_number}</td>
                    <td>{inv.total_amount}</td>
                    <td>{inv.currency || '—'}</td>
                    <td>{inv.country || '—'}</td>
                    <td>
                      <button onClick={() => openEditModal(inv)} style={{ marginRight: '5px' }}>Редактировать</button>
                      <button onClick={() => handleDeleteInvoice(inv.id)}>Удалить</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>

            {/* Пагинация */}
            <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
              <button disabled={invoicePage <= 1} onClick={() => { setInvoicePage(p => p - 1); fetchInvoices(); }}>← Назад</button>
              <span>Стр. {invoicePage} из {totalPages} (всего {totalInvoices})</span>
              <button disabled={invoicePage >= totalPages} onClick={() => { setInvoicePage(p => p + 1); fetchInvoices(); }}>Вперед →</button>
            </div>

            {/* Модальное окно редактирования */}
            {editModalOpen && editingInvoice && (
              <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '40px', zIndex: 1000 }}>
                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', maxWidth: '900px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
                  <h3>Редактирование инвойса {editingInvoice.invoice_number}</h3>
                  <div className="invoice-fields-grid" style={{ gap: '10px' }}>
                    <label>Поставщик:
                      <select value={editForm.supplier_id} onChange={e => setEditForm({...editForm, supplier_id: e.target.value})} required>
                        <option value="">Выберите</option>
                        {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </label>
                    <label>Номер инвойса: <input type="text" value={editForm.invoice_number} onChange={e => setEditForm({...editForm, invoice_number: e.target.value})} /></label>
                    <label>Дата инвойса: <input type="date" value={editForm.invoice_date} onChange={e => setEditForm({...editForm, invoice_date: e.target.value})} /></label>
                    <label>Дата поставки: <input type="date" value={editForm.delivery_date} onChange={e => setEditForm({...editForm, delivery_date: e.target.value})} /></label>
                    <label>Общая сумма: <input type="number" value={editForm.total_amount} onChange={e => setEditForm({...editForm, total_amount: e.target.value})} /></label>
                    <label>AWB: <input type="text" value={editForm.awb_number} onChange={e => setEditForm({...editForm, awb_number: e.target.value})} /></label>
                    <label>Вес поставки (кг): <input type="number" value={editForm.transport_weight} onChange={e => setEditForm({...editForm, transport_weight: e.target.value})} /></label>
                    <label>Стоимость транспорта: <input type="number" value={editForm.transport_cost} onChange={e => setEditForm({...editForm, transport_cost: e.target.value})} /></label>
                    <label>Комиссия поставщика: <input type="number" value={editForm.supplier_commission} onChange={e => setEditForm({...editForm, supplier_commission: e.target.value})} /></label>
                    <label>Стоимость упаковки: <input type="number" value={editForm.packaging_cost} onChange={e => setEditForm({...editForm, packaging_cost: e.target.value})} /></label>
                  </div>

                  {editForm.packaging_details && editForm.packaging_details.length > 0 && (
                    <div style={{ marginTop: '10px', border: '1px solid #ccc', padding: '8px' }}>
                      <h4>Упаковка поставки</h4>
                      <ResponsiveTable border="1" style={{ width: '100%' }}>
                        <thead><tr><th>Количество коробок</th><th>Код коробки</th><th>Тип коробки</th><th>Стоимость за коробки</th></tr></thead>
                        <tbody>
                          {editForm.packaging_details.map((pkg, idx) => (
                            <tr key={idx}><td>{pkg.qua}</td><td>{pkg.code}</td><td>{pkg.description}</td><td>{pkg.amount}</td></tr>
                          ))}
                        </tbody>
                      </ResponsiveTable>
                    </div>
                  )}

                  <h4>Позиции</h4>
                  <ResponsiveTable border="1" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Вид цветка</th><th>Сорт</th><th>Высота</th><th>Разбанчевка</th><th>Тип коробки</th><th>Номер коробки</th><th>Банчей в коробке</th><th>Итого стеблей</th><th>Цена за штуку</th><th>Цена общая</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editForm.items.map((item, idx) => (
                        <tr key={idx}>
                          <td><input type="text" value={item.flower_type} onChange={e => { const newItems = [...editForm.items]; newItems[idx].flower_type = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                          <td><input type="text" value={item.variety} onChange={e => { const newItems = [...editForm.items]; newItems[idx].variety = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                          <td><input type="text" value={item.length} onChange={e => { const newItems = [...editForm.items]; newItems[idx].length = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                          <td><input type="number" value={item.stems_per_bunch} onChange={e => { const newItems = [...editForm.items]; newItems[idx].stems_per_bunch = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                          <td><input type="text" value={item.box_dimension} onChange={e => { const newItems = [...editForm.items]; newItems[idx].box_dimension = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                          <td><input type="text" value={item.box_number} onChange={e => { const newItems = [...editForm.items]; newItems[idx].box_number = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                          <td><input type="number" value={item.bunches_per_box} onChange={e => { const newItems = [...editForm.items]; newItems[idx].bunches_per_box = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                          <td><input type="number" value={item.total_stems} onChange={e => { const newItems = [...editForm.items]; newItems[idx].total_stems = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                          <td><input type="number" value={item.price_per_stem} onChange={e => { const newItems = [...editForm.items]; newItems[idx].price_per_stem = e.target.value; setEditForm({...editForm, items: newItems}); }} /></td>
                          <td><input type="number" value={item.total_line} readOnly /></td>
                        </tr>
                      ))}
                    </tbody>
                  </ResponsiveTable>
                  <div style={{ marginTop: '10px' }}>
                    <button onClick={handleEditSave} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '8px 16px', marginRight: '10px' }}>Сохранить</button>
                    <button onClick={() => setEditModalOpen(false)} style={{ backgroundColor: '#ccc', border: 'none', padding: '8px 16px' }}>Отмена</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      // ---------- Дашборд ----------
      case 'dashboard':
        return <DashboardTab invoices={invoices} payments={payments} suppliers={suppliers} eurRate={eurRate} setEurRate={setEurRate} usdRate={usdRate} setUsdRate={setUsdRate} balanceDate={balanceDate} setBalanceDate={setBalanceDate} />;
      // ---------- Отчёты ----------
      case 'reports':
        return (
          <div>
            <h3>Отчёты</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
              <div><h4>Проверка баланса на дату</h4><label>Дата: <input type="date" value={balanceDate} onChange={e => setBalanceDate(e.target.value)} /></label><br />
                <label>Поставщик: <select value={reportSupplier} onChange={e => setReportSupplier(e.target.value)}><option value="">Все</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
                <p>Баланс: (будет добавлен)</p></div>
              <div><h4>История операций</h4><label>Поставщик: <select value={reportSupplier} onChange={e => setReportSupplier(e.target.value)}><option value="">Все</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
                <label>Месяц: <input type="number" value={reportMonth} onChange={e => setReportMonth(e.target.value)} /></label><label>Год: <input type="number" value={reportYear} onChange={e => setReportYear(e.target.value)} /></label>
                <button>Показать</button><p>(будет добавлен)</p></div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  if (embedded) return <div className="legacy-admin-content">{renderTab()}</div>;
  return (
    <div style={{ display: 'flex' }}>
      <div style={{ width: '220px', background: '#f5f5f5', padding: '15px', minHeight: '100vh' }}>
        <h3>Админ-панель</h3>
        <button onClick={() => { localStorage.clear(); navigate('/login'); }}>Выйти</button>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          <li onClick={() => setActiveTab('users')} style={{ padding: 8, cursor: 'pointer', background: activeTab === 'users' ? '#ddd' : 'transparent' }}>👥 Пользователи</li>
          <li onClick={() => setActiveTab('suppliers')} style={{ padding: 8, cursor: 'pointer', background: activeTab === 'suppliers' ? '#ddd' : 'transparent' }}>🏭 Поставщики</li>
          <li onClick={() => setActiveTab('consignees')} style={{ padding: 8, cursor: 'pointer', background: activeTab === 'consignees' ? '#ddd' : 'transparent' }}>🚚 Грузополучатели</li>
          <li onClick={() => setActiveTab('companies')} style={{ padding: 8, cursor: 'pointer', background: activeTab === 'companies' ? '#ddd' : 'transparent' }}>🏢 Компании</li>
          <li onClick={() => setActiveTab('payments')} style={{ padding: 8, cursor: 'pointer', background: activeTab === 'payments' ? '#ddd' : 'transparent' }}>💵 Внесение оплаты</li>
          <li onClick={() => setActiveTab('invoices')} style={{ padding: 8, cursor: 'pointer', background: activeTab === 'invoices' ? '#ddd' : 'transparent' }}>📄 Инвойсы</li>
          <li onClick={() => setActiveTab('dashboard')} style={{ padding: 8, cursor: 'pointer', background: activeTab === 'dashboard' ? '#ddd' : 'transparent' }}>📊 Дашборд</li>
          <li onClick={() => setActiveTab('reports')} style={{ padding: 8, cursor: 'pointer', background: activeTab === 'reports' ? '#ddd' : 'transparent' }}>📈 Отчёты</li>
        </ul>
      </div>
      <div style={{ flex: 1, padding: '20px' }}>
        {renderTab()}
      </div>
    </div>
  );
}

export default AdminDashboard;
